// EPOCH 35 - Phase 2: RPC Testing Page
// This is a test page for Phase 2 of CSR Campaign Management
// It tests the RPC functions: csr_create_campaign_for_user and csr_update_campaign_for_user

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/client';
import { Link } from 'react-router-dom';
import { Session } from '@supabase/supabase-js';

interface TestResult {
  test: string;
  status: 'pending' | 'running' | 'success' | 'failure';
  message?: string;
  details?: Record<string, unknown>;
}

interface CompanyUser {
  id: string;
  email: string;
  full_name: string;
  company_id: string;
  company_name: string;
}

const CSRCampaignTest = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [isCSR, setIsCSR] = useState(false);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [testResults, setTestResults] = useState<TestResult[]>([
    { test: 'Load assigned users', status: 'pending' },
    { test: 'Create campaign for user', status: 'pending' },
    { test: 'Verify audit log entry', status: 'pending' },
    { test: 'Update campaign for user', status: 'pending' },
    { test: 'Verify update audit log', status: 'pending' },
    { test: 'Test permission denied (non-assigned user)', status: 'pending' },
    { test: 'Test CSR notes field', status: 'pending' },
  ]);
  const [createdCampaignId, setCreatedCampaignId] = useState<string | null>(null);

  const updateTestStatus = useCallback((testName: string, status: TestResult['status'], message?: string, details?: Record<string, unknown>) => {
    setTestResults(prev => prev.map(test => 
      test.test === testName 
        ? { ...test, status, message, details }
        : test
    ));
  }, []);

  const loadAssignedUsers = useCallback(async (csrId: string) => {
    updateTestStatus('Load assigned users', 'running');
    try {
      // Get companies assigned to this CSR
      const { data: companies, error: companiesError } = await supabase
        .from('companies')
        .select('id, name')
        .eq('assigned_csr_id', csrId);

      if (companiesError) throw companiesError;

      if (!companies || companies.length === 0) {
        updateTestStatus('Load assigned users', 'failure', 'No companies assigned to CSR');
        return;
      }

      // Get users from these companies
      const companyIds = companies.map(c => c.id);
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, email, full_name, company_id')
        .in('company_id', companyIds)
        .eq('role', 'user');

      if (usersError) throw usersError;

      // Map company names to users
      const usersWithCompanies = usersData?.map(user => ({
        ...user,
        company_name: companies.find(c => c.id === user.company_id)?.name || 'Unknown'
      })) || [];

      setUsers(usersWithCompanies);
      updateTestStatus('Load assigned users', 'success', `Loaded ${usersWithCompanies.length} users`);
    } catch (error) {
      updateTestStatus('Load assigned users', 'failure', error instanceof Error ? error.message : 'Unknown error');
    }
  }, [updateTestStatus]);

  const checkAuth = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }
      setSession(session);

      // Check if user is CSR
      const { data: userData } = await supabase
        .from('users')
        .select('role')
        .eq('id', session.user.id)
        .single();

      if (userData?.role === 'csr') {
        setIsCSR(true);
        await loadAssignedUsers(session.user.id);
      }
    } catch (error) {
      console.error('Auth check error:', error);
    } finally {
      setLoading(false);
    }
  }, [loadAssignedUsers]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const runAllTests = async () => {
    if (!selectedUser) {
      alert('Please select a user first');
      return;
    }

    // Test 1: Create campaign for user
    const campaignId = await testCreateCampaign();
    
    // Only proceed with other tests if campaign was created
    if (campaignId) {
      await testVerifyAuditLog(campaignId, 'create');
      await testUpdateCampaign(campaignId);
      await testVerifyAuditLog(campaignId, 'update');
    }

    await testPermissionDenied();
  };

  const testCreateCampaign = async () => {
    updateTestStatus('Create campaign for user', 'running');
    try {
      // Additional campaign data beyond the required fields
      const additionalData = {
        description: 'Test campaign created by CSR on behalf of user',
        target_audience: 'CTOs',
        service_tier: 'junior_sdr'
      };

      const { data, error } = await supabase.rpc('csr_create_campaign_for_user', {
        p_user_id: selectedUser,
        p_name: `Test Campaign - Created by CSR at ${new Date().toISOString()}`,
        p_data: additionalData,
        p_csr_notes: 'This campaign was created during Phase 2 testing of Epoch 35'
      }).single(); // Add .single() since RPC returns one row

      if (error) throw error;

      if (data) {
        setCreatedCampaignId(data.id);
        updateTestStatus('Create campaign for user', 'success', 
          `Campaign created: ${data.id}`, data);
        return data.id; // Return the campaign ID
      } else {
        throw new Error('No data returned from RPC');
      }
    } catch (error) {
      updateTestStatus('Create campaign for user', 'failure', error instanceof Error ? error.message : 'Unknown error');
      setCreatedCampaignId(null);
      return null; // Return null on failure
    }
  };

  const testVerifyAuditLog = async (campaignId: string, action: 'create' | 'update') => {
    const testName = action === 'create' ? 'Verify audit log entry' : 'Verify update audit log';
    updateTestStatus(testName, 'running');
    
    try {
      // Query the audit log
      const { data, error } = await supabase
        .from('campaign_audit_log')
        .select('*')
        .eq('campaign_id', campaignId)
        .eq('action_type', action === 'create' ? 'created' : 'updated')
        .order('timestamp', { ascending: false })
        .limit(1)
        .single();

      if (error) throw error;

      if (data) {
        updateTestStatus(testName, 'success', 
          `Audit log verified: ${action} action recorded`, data);
      } else {
        throw new Error('No audit log entry found');
      }
    } catch (error) {
      updateTestStatus(testName, 'failure', error instanceof Error ? error.message : 'Unknown error');
    }
  };

  const testUpdateCampaign = async (campaignId: string) => {
    updateTestStatus('Update campaign for user', 'running');
    updateTestStatus('Test CSR notes field', 'running');
    
    try {
      const updates = {
        name: `Updated Test Campaign - Modified by CSR at ${new Date().toISOString()}`,
        description: 'Updated description by CSR',
        csr_notes: 'Campaign updated during Phase 2 testing. Added more details for user.'
      };

      const { data, error } = await supabase.rpc('csr_update_campaign_for_user', {
        p_campaign_id: campaignId,
        p_updates: updates,
        p_reason: 'Testing Phase 2 update functionality'
      }).single(); // Add .single() since RPC returns one row

      if (error) throw error;

      if (data) {
        updateTestStatus('Update campaign for user', 'success', 
          'Campaign updated successfully', data);
        
        // Check if CSR notes were saved (they should be appended with the reason)
        if (data.csr_notes && data.csr_notes.includes('Testing Phase 2 update functionality')) {
          updateTestStatus('Test CSR notes field', 'success', 'CSR notes saved correctly with update reason');
        } else {
          updateTestStatus('Test CSR notes field', 'failure', 'CSR notes not saved properly');
        }
      } else {
        throw new Error('No data returned from update RPC');
      }
    } catch (error) {
      updateTestStatus('Update campaign for user', 'failure', error instanceof Error ? error.message : 'Unknown error');
      updateTestStatus('Test CSR notes field', 'failure', 'Could not test due to update failure');
    }
  };

  const testPermissionDenied = async () => {
    updateTestStatus('Test permission denied (non-assigned user)', 'running');
    
    try {
      // Try to create a campaign for a fake user ID (should fail)
      const fakeUserId = '00000000-0000-0000-0000-000000000000';
      
      const { error } = await supabase.rpc('csr_create_campaign_for_user', {
        p_user_id: fakeUserId,
        p_name: 'Should Fail',
        p_data: {},
        p_csr_notes: 'This should be denied'
      });

      if (error) {
        // We expect an error here
        updateTestStatus('Test permission denied (non-assigned user)', 'success', 
          'Permission correctly denied: ' + error.message);
      } else {
        // If no error, the permission check failed
        updateTestStatus('Test permission denied (non-assigned user)', 'failure', 
          'Permission check failed - campaign was created when it should not have been');
      }
    } catch (error) {
      // This is expected
      updateTestStatus('Test permission denied (non-assigned user)', 'success', 
        'Permission correctly denied: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'pending': {
        return <span className="text-gray-400 text-lg">⚪</span>;
      }
      case 'running': {
        return <span className="text-blue-500 text-lg animate-spin inline-block">⚙️</span>;
      }
      case 'success': {
        return <span className="text-green-500 text-lg">✅</span>;
      }
      case 'failure': {
        return <span className="text-red-500 text-lg">❌</span>;
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-blue-500 text-2xl animate-spin">⚙️</div>
      </div>
    );
  }

  if (!session || !isCSR) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">This test page is only accessible to CSR users.</p>
          <Link to="/dashboard" className="mt-4 inline-flex items-center text-blue-600 hover:underline">
            <span className="mr-2">←</span>
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to="/csr-dashboard" className="inline-flex items-center text-blue-600 hover:underline mb-4">
          <span className="mr-2">←</span>
          Back to CSR Dashboard
        </Link>
        
        <h1 className="text-3xl font-bold text-gray-900">
          Epoch 35 - Phase 2: RPC Function Testing
        </h1>
        <p className="text-gray-600 mt-2">
          Testing backend security and RPC functions for CSR campaign management
        </p>
      </div>

      {/* User Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Select a User</h2>
        <p className="text-gray-600 mb-4">
          Select a user from your assigned companies to test campaign creation on their behalf:
        </p>
        
        {users.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
            <p className="text-yellow-800">
              No users found in your assigned companies. Make sure you have companies assigned with active users.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Select a user --</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.full_name || user.email} ({user.company_name})
                </option>
              ))}
            </select>
            
            {selectedUser && (
              <button
                onClick={runAllTests}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Run All Tests
              </button>
            )}
          </div>
        )}
      </div>

      {/* Test Results */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold mb-4">Test Results</h2>
        
        <div className="space-y-3">
          {testResults.map((result, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  {getStatusIcon(result.status)}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{result.test}</div>
                  {result.message && (
                    <div className={`text-sm mt-1 ${
                      result.status === 'success' ? 'text-green-600' :
                      result.status === 'failure' ? 'text-red-600' :
                      'text-gray-600'
                    }`}>
                      {result.message}
                    </div>
                  )}
                  {result.details && (
                    <details className="mt-2">
                      <summary className="text-sm text-blue-600 cursor-pointer hover:underline">
                        View Details
                      </summary>
                      <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-x-auto">
                        {JSON.stringify(result.details, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Test Summary */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Test Coverage</h3>
        <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
          <li>RPC function: csr_create_campaign_for_user</li>
          <li>RPC function: csr_update_campaign_for_user</li>
          <li>Audit logging for create and update actions</li>
          <li>Permission checks for non-assigned users</li>
          <li>CSR notes field persistence</li>
          <li>Campaign provenance tracking (created_by, created_for, creation_context)</li>
        </ul>
      </div>

      {/* Created Campaign ID Display */}
      {createdCampaignId && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="font-semibold text-green-900 mb-2">Test Campaign Created</h3>
          <p className="text-sm text-green-800">
            Campaign ID: <code className="bg-white px-2 py-1 rounded">{createdCampaignId}</code>
          </p>
          <p className="text-sm text-green-600 mt-2">
            You can verify this campaign in the database or through the campaign management interface.
          </p>
        </div>
      )}
    </div>
  );
};

export default CSRCampaignTest;